<template>
  <SettingsLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Informations de la boutique</h1>
          <small class="text-muted">
            Ces informations seront utilisées sur les tickets, factures et dans l’application.
          </small>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <form @submit.prevent="handleSubmit" class="card border-0 shadow-sm">
        <div class="card-body">
          <!-- Ligne 1 : nom + raison sociale -->
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="form-label small fw-semibold">Nom commercial *</label>
              <PrimevueInputText
                v-model="form.name"
                class="w-100"
                placeholder="Ex: Superette Suite241"
                required
              />
              <small class="text-muted">
                S’affiche dans la navbar et sur les tickets.
              </small>
            </div>

            <div class="col-md-6">
              <label class="form-label small fw-semibold">Raison sociale</label>
              <PrimevueInputText
                v-model="form.legal_name"
                class="w-100"
                placeholder="Nom légal de l’entreprise (optionnel)"
              />
            </div>
          </div>

          <!-- Ligne 2 : téléphone / email -->
          <div class="row g-3 mb-3">
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Téléphone *</label>
              <PrimevueInputText
                v-model="form.phone"
                class="w-100"
                type="tel"
                placeholder="Ex: +241 xx xx xx xx"
                required
                @input="sanitizePhoneField"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">WhatsApp</label>
              <PrimevueInputText
                v-model="form.whatsapp_phone"
                class="w-100"
                type="tel"
                placeholder="Ex: +241 xx xx xx xx"
                @input="sanitizeWhatsAppField"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Email</label>
              <PrimevueInputText
                v-model="form.email"
                class="w-100"
                placeholder="Ex: contact@boutique.ga"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Devise</label>
              <PrimevueDropdown
                v-model="form.currency_code"
                :options="currencyOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Position devise</label>
              <PrimevueDropdown
                v-model="form.currency_position"
                :options="currencyPositionOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Décimales</label>
              <PrimevueDropdown
                v-model="form.decimal_places"
                :options="decimalPlaceOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Séparateur décimal</label>
              <PrimevueDropdown
                v-model="form.decimal_separator"
                :options="decimalSeparatorOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Séparateur milliers</label>
              <PrimevueDropdown
                v-model="form.thousands_separator"
                :options="thousandsSeparatorOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>
            <div class="col-12">
              <small class="text-muted">
                Aperçu : {{ $format.currency(1234567.89, form.currency_code) }}
              </small>
            </div>
          </div>

          <!-- Ligne 3 : adresse -->
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="form-label small fw-semibold">Adresse *</label>
              <PrimevueInputText
                v-model="form.address"
                class="w-100"
                placeholder="Adresse complète"
                required
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Ville</label>
              <PrimevueInputText
                v-model="form.city"
                class="w-100"
                placeholder="Ex: Libreville"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold">Pays</label>
              <PrimevueInputText
                v-model="form.country"
                class="w-100"
                placeholder="Ex: Gabon"
              />
            </div>
          </div>

          <!-- Ligne 4 : infos légales -->
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <label class="form-label small fw-semibold">N° contribuable / NIF</label>
              <PrimevueInputText
                v-model="form.tax_id"
                class="w-100"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label small fw-semibold">Registre de commerce</label>
              <PrimevueInputText
                v-model="form.trade_register"
                class="w-100"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label small fw-semibold">URL logo</label>
              <PrimevueInputText
                v-model="form.logo_url"
                class="w-100"
                placeholder="Ex: /storage/logo.png"
              />
            </div>
          </div>

          <!-- Ligne 5 : TVA / CSS / fuseau -->
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <label class="form-label small fw-semibold">Taux TVA (%) *</label>
              <PrimevueInputNumber
                v-model="form.tva_rate"
                class="w-100"
                mode="decimal"
                :min="0"
                :max="100"
                :maxFractionDigits="2"
                placeholder="Ex: 0,18"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label small fw-semibold">Taux CSS (%) *</label>
              <PrimevueInputNumber
                v-model="form.css_rate"
                class="w-100"
                mode="decimal"
                :min="0"
                :max="100"
                :maxFractionDigits="2"
                placeholder="Ex: 0,01"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label small fw-semibold">Fuseau horaire *</label>
              <PrimevueDropdown
                v-model="form.timezone"
                :options="timezoneOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Sélectionner un fuseau"
                class="w-100"
              />
              <small class="text-muted">
                Utilisé pour les dates/heures dans tout le système.
              </small>
            </div>
          </div>

          <!-- Ligne 6 : footer -->
          <div class="mb-3">
            <label class="form-label small fw-semibold">Texte de pied de page des tickets / factures</label>
            <PrimevueTextarea
              v-model="form.footer_note"
              class="w-100"
              rows="2"
              autoResize
              placeholder="Ex: Merci pour votre confiance, aucun retour sans ticket..."
            />
          </div>

          <div class="form-check form-switch mb-1">
            <input
              id="cash-count-enabled"
              v-model="form.cash_count_enabled"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label fw-semibold" for="cash-count-enabled">
              Activer le billetage en fin de journée
            </label>
          </div>
          <small class="text-muted d-block">
            Si cette option est désactivée, la caissière clôture sa journée avec un commentaire facultatif, sans détail billets/pièces.
          </small>

          <div class="form-check form-switch mb-1 mt-3">
            <input
              id="cash-register-auto-close-enabled"
              v-model="form.cash_register_auto_close_enabled"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label fw-semibold" for="cash-register-auto-close-enabled">
              Fermer automatiquement les sessions de caisse en fin de journée
            </label>
          </div>
          <small class="text-muted d-block">
            Activée : les sessions ouvertes se ferment automatiquement à 23:59:59 selon le fuseau horaire du site.
            Désactivée : une session peut rester ouverte plusieurs jours, jusqu’à la clôture de journée ou la déconnexion caisse.
          </small>
        </div>

        <div class="card-footer bg-white d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            @click="cancel"
            :disabled="isSubmitting"
          >
            <i class="fa fa-xmark" aria-hidden="true"></i>
            <span class="btn-label ms-1">Annuler</span>
          </button>
          <AppLoadingButton
            type="submit"
            class="btn btn-primary btn-sm"
            :loading="isSubmitting"
            loading-label="Enregistrement..."
          >
            <i class="fa fa-save" aria-hidden="true"></i>
            <span class="btn-label ms-1">Enregistrer</span>
          </AppLoadingButton>
        </div>
      </form>
    </template>
  </SettingsLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isPhoneInputValid, phoneInputValidationMessage, sanitizePhoneInput } from '@/utils/phone';

export default {
  name: 'ShopInfoView',
  data() {
    return {
      form: {
        name: '',
        legal_name: '',
        phone: '',
        whatsapp_phone: '',
        email: '',
        address: '',
        city: '',
        country: '',
        tax_id: '',
        trade_register: '',
        currency_code: 'FCFA',
        currency_position: 'after',
        decimal_places: 0,
        decimal_separator: ',',
        thousands_separator: 'space',
        logo_url: '',
        footer_note: '',
        timezone: 'Africa/Libreville',
        tva_rate: 18,
        css_rate: 1,
        cash_count_enabled: true,
        cash_register_auto_close_enabled: false,
      },
      isSubmitting: false,
      errorMessage: '',
      successMessage: '',
      currencyOptions: [
        { label: 'FCFA', value: 'FCFA' },
        { label: 'XAF', value: 'XAF' },
        { label: 'EUR', value: 'EUR' },
        { label: 'USD', value: 'USD' },
      ],
      currencyPositionOptions: [
        { label: 'Après le montant', value: 'after' },
        { label: 'Avant le montant', value: 'before' },
      ],
      decimalPlaceOptions: [
        { label: '0', value: 0 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
      ],
      decimalSeparatorOptions: [
        { label: 'Virgule (,)', value: ',' },
        { label: 'Point (.)', value: '.' },
      ],
      thousandsSeparatorOptions: [
        { label: 'Espace', value: 'space' },
        { label: 'Point', value: 'dot' },
        { label: 'Virgule', value: 'comma' },
        { label: 'Aucun', value: 'none' },
      ],
      timezoneOptions: [
        { label: 'Africa/Libreville (Gabon)', value: 'Africa/Libreville' },
        { label: 'Africa/Douala', value: 'Africa/Douala' },
        { label: 'Africa/Abidjan', value: 'Africa/Abidjan' },
        { label: 'UTC', value: 'UTC' },
      ],
    };
  },
  computed: {
    ...mapGetters('shop', ['shopInfo']),
  },
  mounted() {
    this.initFormFromStore();
  },
  methods: {
    ...mapActions('shop', ['saveInfo']),

    initFormFromStore() {
      if (!this.shopInfo) return;

      const s = this.shopInfo;
      this.form = {
        name: s.name || '',
        legal_name: s.legal_name || '',
        phone: s.phone || '',
        whatsapp_phone: s.whatsapp_phone || '',
        email: s.email || '',
        address: s.address || '',
        city: s.city || '',
        country: s.country || '',
        tax_id: s.tax_id || '',
        trade_register: s.trade_register || '',
        currency_code: s.currency_code || 'FCFA',
        currency_position: s.currency_position || 'after',
        decimal_places: s.decimal_places != null ? Number(s.decimal_places) : 0,
        decimal_separator: s.decimal_separator || ',',
        thousands_separator: this.separatorToKey(s.thousands_separator),
        logo_url: s.logo_url || '',
        footer_note: s.footer_note || '',
        timezone: s.timezone || 'Africa/Libreville',
        tva_rate: s.tva_rate != null ? Number(s.tva_rate) : 18,
        css_rate: s.css_rate != null ? Number(s.css_rate) : 1,
        cash_count_enabled: s.cash_count_enabled !== false,
        cash_register_auto_close_enabled: s.cash_register_auto_close_enabled === true,
      };
    },

    sanitizePhoneField(event) {
      this.form.phone = sanitizePhoneInput(event?.target?.value ?? this.form.phone);
    },

    sanitizeWhatsAppField(event) {
      this.form.whatsapp_phone = sanitizePhoneInput(event?.target?.value ?? this.form.whatsapp_phone);
    },

    async handleSubmit() {
      if (this.isSubmitting) return;

      this.errorMessage = '';
      this.successMessage = '';

      if (!isPhoneInputValid(this.form.phone)) {
        this.errorMessage = phoneInputValidationMessage('téléphone de la boutique');
        return;
      }

      if (!isPhoneInputValid(this.form.whatsapp_phone)) {
        this.errorMessage = phoneInputValidationMessage('numéro WhatsApp de la boutique');
        return;
      }

      this.form.phone = sanitizePhoneInput(this.form.phone);
      this.form.whatsapp_phone = sanitizePhoneInput(this.form.whatsapp_phone);
      this.isSubmitting = true;

      try {
        const payload = { ...this.form };
        await this.saveInfo(payload);
        this.successMessage = 'Informations boutique enregistrées avec succès.';
        // On peut rester sur la page, ou revenir en arrière :
        // this.$router.back();
      } catch (error) {
        console.error('Erreur enregistrement infos boutique', error);
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          this.errorMessage = Object.values(errors).flat().join(' ');
        } else {
          this.errorMessage = 'Une erreur est survenue lors de la sauvegarde.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    cancel() {
      this.$router.back();
    },

    separatorToKey(value) {
      if (value === '.') return 'dot';
      if (value === ',') return 'comma';
      if (value === '') return 'none';
      return 'space';
    },
  },
};
</script>
